import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import { useHistory } from 'react-router';
import useSWR from 'swr';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import Button from '../../../common/base/Button';
import Icon from '../../../common/base/Icon';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import { Div } from '../../../common/helpers/StyledUtils';
import Search from '../../../common/Search';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, StyledViewNavSecondary } from '../../../layouts/ViewLayout';
import CreateTenantModal from './CreateTenantModal';
import TenantsList from './TenantsList';
import { extractFromArray } from '../../../../utils';
import useSearchQuery from '../../../hooks/useSearchQuery';
import DropdownMenu from '../../../common/DropdownMenu';
import { filterOutpostTenantBySearchTerm } from '../utils/tenantDisplay';
const limit = 100;
export interface TenantsListQueryParams {
  // Pagination & sorting
  order_by?: string | string[];
  next?: string | string[];
  prev?: string | string[];

  // Filters
  search_term?: string | string[];

  // Actions
  new?: null;
}
const sort_options = {
  created_at_desc: {
    label: 'Newest -> Oldest',
    key: 'created_at',
    dir: 'desc'
  },
  created_at_asc: {
    label: 'Oldest -> Newest',
    key: 'created_at',
    dir: 'asc'
  }
} as const;
const sort_keys = Object.keys(sort_options) as (keyof typeof sort_options)[];
interface TenantsListFiltersProps {
  order?: 'asc' | 'desc';
  next?: string;
  prev?: string;
  limit?: number;
}
const extractFiltersFromQuery = (parsed_query: TenantsListQueryParams): TenantsListFiltersProps => {
  const order_by = extractFromArray(parsed_query.order_by || []);
  return {
    order: order_by ? sort_options[order_by].dir : undefined,
    next: extractFromArray(parsed_query.next || []) as string,
    prev: extractFromArray(parsed_query.prev || []) as string,
    limit: limit
  };
};
const TenantsView: React.FC = () => {
  const $ = _c(53);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const history = useHistory();
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  let t0;
  if ($[0] !== query || $[1] !== updateSearchQuery) {
    t0 = term => {
      updateSearchQuery({
        ...query,
        search_term: term || undefined
      });
    };
    $[0] = query;
    $[1] = updateSearchQuery;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const setSearchTerm = t0;
  const filters = extractFiltersFromQuery(query);
  const search_term = extractFromArray(query.search_term || []) as string;
  let t1;
  if ($[3] !== HookdeckAPI.outpost.tenants || $[4] !== filters) {
    t1 = () => HookdeckAPI.outpost.tenants.list(filters);
    $[3] = HookdeckAPI.outpost.tenants;
    $[4] = filters;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    data: tenants,
    mutate,
    isLoading: is_loading
  } = useSWR(APIMethodKeys.outpost.tenants.list(filters), t1);
  let t2;
  if ($[6] !== HookdeckAPI.outpost.tenants || $[7] !== search_term) {
    t2 = () => HookdeckAPI.outpost.tenants.get(search_term);
    $[6] = HookdeckAPI.outpost.tenants;
    $[7] = search_term;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    data: tenant
  } = useSWR(search_term ? APIMethodKeys.outpost.tenants.get(search_term) : null, t2);
  let t3;
  if ($[9] !== search_term || $[10] !== tenant || $[11] !== tenants?.models) {
    t3 = tenant ? [tenant] : tenants?.models?.filter(tenant_0 => {
      if (!search_term) {
        return true;
      }
      return filterOutpostTenantBySearchTerm(tenant_0, search_term);
    });
    $[9] = search_term;
    $[10] = tenant;
    $[11] = tenants?.models;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const filtered_tenants = t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      y: 5
    };
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      align: "center",
      justify: "space-between",
      gap: 2
    };
    t6 = <Text semi={true} ellipsis={true}>Tenants</Text>;
    $[14] = t5;
    $[15] = t6;
  } else {
    t5 = $[14];
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Div flex={t5}>{t6}<Tooltip tooltip="A tenant is an entity in your product that you publish events to, typically an org, team, or user in your system. Outpost routes those events to the tenant's destinations, which are the webhook endpoints or services where events are delivered." side="bottom" max_width={380}><Icon icon="info" muted={true} /></Tooltip></Div>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <StyledViewNavHeader p={t4}>{t7}<StyledViewNavActions><Button.Permission to="/tenants?new" primary={true} icon="add_circle">New tenant</Button.Permission></StyledViewNavActions></StyledViewNavHeader>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      b: 3
    };
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      justify: "space-between",
      align: "center",
      gap: 3
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = search_term || "";
  let t12;
  if ($[20] !== setSearchTerm || $[21] !== t11) {
    t12 = <Search onChange={setSearchTerm} value={t11} placeholder="Filter by tenant ID or name..." small={true} />;
    $[20] = setSearchTerm;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== query.order_by || $[24] !== updateSearchQuery) {
    t13 = sort_keys.map(key => ({
      onClick: () => updateSearchQuery({
        order_by: key
      }),
      label: sort_options[key]?.label,
      selected: query.order_by === key
    }));
    $[23] = query.order_by;
    $[24] = updateSearchQuery;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      min: 150
    };
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = sort_options[extractFromArray(query.order_by) || "created_at_desc"]?.label;
  let t16;
  if ($[27] !== t13 || $[28] !== t15) {
    t16 = <DropdownMenu options={t13} p={0} parent_width={t14} placement="bottom-end" label_prefix="Order by" label={t15} expand_icon="chevron_down" icon="sort" outline={true} />;
    $[27] = t13;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t12 || $[31] !== t16) {
    t17 = <StyledViewNav>{t8}<StyledViewNavSecondary p={t9}><Div w={100} flex={t10}>{t12}{t16}</Div></StyledViewNavSecondary></StyledViewNav>;
    $[30] = t12;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== filtered_tenants) {
    t18 = filtered_tenants || [];
    $[33] = filtered_tenants;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = tenants?.pagination.next || undefined;
  const t20 = tenants?.pagination.prev || undefined;
  let t21;
  if ($[35] !== t19 || $[36] !== t20) {
    t21 = {
      limit,
      next: t19,
      prev: t20
    };
    $[35] = t19;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  const t22 = !!search_term;
  const t23 = tenants?.count || 0;
  let t24;
  if ($[38] !== is_loading || $[39] !== t18 || $[40] !== t21 || $[41] !== t22 || $[42] !== t23) {
    t24 = <TenantsList tenants={t18} pagination={t21} loading={is_loading} filtering={t22} total={t23} />;
    $[38] = is_loading;
    $[39] = t18;
    $[40] = t21;
    $[41] = t22;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== history || $[45] !== mutate || $[46] !== query.new || $[47] !== tenants?.models) {
    t25 = query.new === null && <CreateTenantModal existing_tenant_ids={tenants?.models?.map(_temp) || []} mutateTenants={mutate} onClose={() => history.push("/tenants")} />;
    $[44] = history;
    $[45] = mutate;
    $[46] = query.new;
    $[47] = tenants?.models;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t17 || $[50] !== t24 || $[51] !== t25) {
    t26 = <StyledViewLayout><StyledViewContent>{t17}{t24}{t25}</StyledViewContent></StyledViewLayout>;
    $[49] = t17;
    $[50] = t24;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  return t26;
};
export default TenantsView;
function _temp(t) {
  return t.id;
}